import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Clock, RefreshCw, Home } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying, success, failed, pending
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    verifyPayment();
  }, []);

  const verifyPayment = async () => {
    const razorpay_payment_id = searchParams.get("razorpay_payment_id");
    const razorpay_payment_link_id = searchParams.get("razorpay_payment_link_id");
    const razorpay_payment_link_status = searchParams.get("razorpay_payment_link_status");
    const razorpay_signature = searchParams.get("razorpay_signature");
    const error_code = searchParams.get("error_code");
    const error_description = searchParams.get("error_description");

    // Check for error in callback
    if (error_code) {
      setStatus("failed");
      setError(error_description || "Payment was not completed");
      return;
    }

    // Check payment link status
    if (razorpay_payment_link_status === "paid" && razorpay_payment_id) {
      try {
        // Try to get payment status from backend
        const response = await axios.get(`${API}/payment/status/${razorpay_payment_id}`);
        setPaymentDetails(response.data);
        
        if (response.data.status === "completed") {
          setStatus("success");
        } else if (response.data.status === "failed") {
          setStatus("failed");
          setError(response.data.error_description || "Payment failed");
        } else {
          // Payment might be processing via webhook
          setStatus("pending");
          // Poll for status
          pollPaymentStatus(response.data.order_id);
        }
      } catch (err) {
        // If we can't find payment by ID, it might still be processing
        setStatus("pending");
        setTimeout(() => {
          setStatus("success"); // Assume success if callback had paid status
        }, 3000);
      }
    } else if (razorpay_payment_link_status === "cancelled") {
      setStatus("failed");
      setError("Payment was cancelled");
    } else {
      setStatus("pending");
    }
  };

  const pollPaymentStatus = async (orderId, attempts = 0) => {
    if (attempts >= 5) {
      setStatus("pending");
      return;
    }

    try {
      const response = await axios.get(`${API}/payment/status/${orderId}`);
      if (response.data.status === "completed") {
        setStatus("success");
        setPaymentDetails(response.data);
      } else if (response.data.status === "failed") {
        setStatus("failed");
        setError(response.data.error_description || "Payment failed");
      } else {
        setTimeout(() => pollPaymentStatus(orderId, attempts + 1), 2000);
      }
    } catch (err) {
      setTimeout(() => pollPaymentStatus(orderId, attempts + 1), 2000);
    }
  };

  const renderContent = () => {
    switch (status) {
      case "verifying":
        return (
          <>
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <RefreshCw className="w-8 h-8 text-slate-600 animate-spin" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-slate-900 mb-2">
              Verifying Payment
            </h2>
            <p className="text-slate-600">Please wait while we confirm your payment...</p>
          </>
        );

      case "success":
        return (
          <>
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-green-800 mb-2">
              Payment Successful!
            </h2>
            <p className="text-slate-600 mb-4">
              Your payment has been received. Your membership application is now being processed.
            </p>
            {paymentDetails && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-slate-500">Amount:</span>
                  <span className="font-medium">₹{paymentDetails.amount}</span>
                  <span className="text-slate-500">Order ID:</span>
                  <span className="font-mono text-xs">{paymentDetails.order_id}</span>
                </div>
              </div>
            )}
            <Button onClick={() => navigate("/dashboard")} className="w-full">
              <Home className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>
          </>
        );

      case "failed":
        return (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-red-800 mb-2">
              Payment Failed
            </h2>
            <p className="text-slate-600 mb-2">
              {error || "Your payment could not be processed."}
            </p>
            <p className="text-sm text-slate-500 mb-6">
              Please try again or contact support if the issue persists.
            </p>
            <div className="space-y-3">
              <Button onClick={() => navigate("/dashboard")} variant="outline" className="w-full">
                Return to Dashboard
              </Button>
              <p className="text-xs text-slate-400">
                Contact: office@cpsindia.org.in
              </p>
            </div>
          </>
        );

      case "pending":
        return (
          <>
            <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-yellow-800 mb-2">
              Payment Processing
            </h2>
            <p className="text-slate-600 mb-4">
              Your payment is being processed. This may take a few moments.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              You will receive a confirmation email once the payment is verified.
            </p>
            <Button onClick={() => navigate("/dashboard")} className="w-full">
              <Home className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md border border-slate-200">
        <CardContent className="p-8 text-center">
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentCallback;
